const EW_LAYOUT_MOTION = Object.freeze({
  thumbnailMs: 336,
  revealMs: 336,
  imageWaitMs: 3000,
  millisecondsPerSecond: 1000,
  zero: 0,
  opaque: 1,
  overlayStack: 2,
  edgeInset: '0',
  easing: 'cubic-bezier(.4, 0, .2, 1)',
});

if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        if (!this.elements.thumbnails) return;

        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
      }

      onSlideChanged(event) {
        if (this.layoutSelection) return;
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      cancelLayoutSelection(restoreThumbnail = false) {
        this.layoutSelection = null;
        delete this.dataset.ewLayoutStage;
        this.layoutFade?.cancel();
        this.layoutFade = null;
        if (restoreThumbnail) {
          const activeId = this.elements.viewer.querySelector('.is-active')?.dataset.mediaId;
          this.setActiveThumbnail(this.elements.thumbnails?.querySelector(`[data-target="${activeId}"]`));
        }
      }

      async selectLayoutMedia(mediaId) {
        this.cancelLayoutSelection();
        const target = this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`);
        const thumbnail = this.elements.thumbnails?.querySelector(`[data-target="${mediaId}"]`);
        if (!target || !thumbnail || target.classList.contains('is-active') ||
            window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
          this.setActiveMedia(mediaId, false);
          return;
        }

        const selection = {};
        this.layoutSelection = selection;
        this.dataset.ewLayoutStage = 'thumbnails';
        const style = getComputedStyle(this);
        const duration = style.getPropertyValue('--ew-gallery-fold-motion').trim();
        const thumbnailMs = Number.parseFloat(duration) *
          (duration.endsWith('ms') ? EW_LAYOUT_MOTION.opaque : EW_LAYOUT_MOTION.millisecondsPerSecond);
        this.setActiveThumbnail(thumbnail);
        const foldFinished = new Promise(resolve => window.setTimeout(resolve,
          Number.isFinite(thumbnailMs) ? thumbnailMs : EW_LAYOUT_MOTION.thumbnailMs));

        // Prepare the full photo while the thumbnail unfolds; retain the current image until ready.
        const image = target.querySelector('.product__media img');
        let imageTimer;
        const photoReady = image ? new Promise(resolve => {
          image.loading = 'eager';
          imageTimer = window.setTimeout(resolve, EW_LAYOUT_MOTION.imageWaitMs);
          image.decode().catch(() => {}).then(resolve);
        }).finally(() => window.clearTimeout(imageTimer)) : Promise.resolve();
        await Promise.all([foldFinished, photoReady]);
        if (this.layoutSelection !== selection || !this.isConnected) return;

        const outgoing = this.createLayoutOverlay();
        this.setActiveMedia(mediaId, false);
        if (!outgoing) return;
        this.elements.viewer.appendChild(outgoing);
        this.dataset.ewLayoutStage = 'reveal';
        const fade = outgoing.animate(
          [{ opacity: EW_LAYOUT_MOTION.opaque }, { opacity: EW_LAYOUT_MOTION.zero }],
          { duration: EW_LAYOUT_MOTION.revealMs, easing: EW_LAYOUT_MOTION.easing, fill: 'forwards' }
        );
        this.layoutFade = fade;
        const finish = () => {
          outgoing.remove();
          if (this.layoutFade !== fade) return;
          this.layoutFade = null;
          delete this.dataset.ewLayoutStage;
        };
        fade.onfinish = finish;
        fade.oncancel = finish;
      }

      createLayoutOverlay() {
        const active = this.elements.viewer.querySelector('.product__media-item.is-active');
        const source = active?.querySelector('video') || active?.querySelector('.product__media img');
        if (!source) return null;
        const src = source.tagName === 'VIDEO' ? source.poster : (source.currentSrc || source.src);
        const box = source.getBoundingClientRect();
        if (!src || !box.width || !box.height) return null;
        const frame = this.elements.viewer.getBoundingClientRect();
        const style = getComputedStyle(source);
        const overlay = document.createElement('div');
        overlay.className = 'ew-gallery-crossfade ew-layout-crossfade';
        overlay.setAttribute('aria-hidden', 'true');
        Object.assign(overlay.style, {
          position: 'absolute', inset: EW_LAYOUT_MOTION.edgeInset,
          backgroundColor: getComputedStyle(document.body).backgroundColor,
          overflow: 'hidden', pointerEvents: 'none', zIndex: String(EW_LAYOUT_MOTION.overlayStack),
        });
        const image = document.createElement('img');
        image.src = src;
        image.alt = '';
        Object.assign(image.style, {
          position: 'absolute', left: `${box.left - frame.left}px`, top: `${box.top - frame.top}px`,
          width: `${box.width}px`, height: `${box.height}px`, objectFit: style.objectFit,
          objectPosition: style.objectPosition, borderRadius: style.borderRadius,
        });
        overlay.appendChild(image);
        return overlay;
      }

      setActiveMedia(mediaId, prepend) {
        this.cancelLayoutSelection();
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) {
          return;
        }
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.firstChild !== activeThumbnail && activeThumbnail.parentElement.prepend(activeThumbnail);
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Don't scroll if the image is already in view
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (!deferredMedia) return;

        const video = deferredMedia.querySelector('video');
        if (video) {
          const VIDEO_START_TIME = 0;
          video.currentTime = VIDEO_START_TIME;
          video.play().catch(() => {});
          return;
        }

        deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
